import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  ContentStepV2,
  PricingStepV2,
  ScheduleStepV2,
  MarketingStepV2,
  ReviewStepV2,
} from "./steps";
import { CopyFromProgramType } from "./CopyFromProgramType";
import type {
  DeliveryModeV2,
} from "@/types/program-v2";

import { toast } from "sonner";
import { transformToAPIFormatV2 } from "@/types/program-v2";
import { useProgramStore } from "@/stores/programStore";
import { pickProgramCommonFieldsForSave } from "@/utils/programTransform";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Validates that Square plans are created for all program types
 */
const validateSquarePlans = (programTypes: any) => {
  const programTypeKeys = Object.keys(programTypes);
  
  if (programTypeKeys.length === 0) {
    return {
      isValid: false,
      error: "No program types configured. Please complete the program setup first."
    };
  }

  for (const type of programTypeKeys) {
    const typeData = programTypes[type];
    if (!typeData?.paymentOptions || typeData.paymentOptions.length === 0) {
      return {
        isValid: false,
        error: `${type} program type has no payment options. Please configure payment options first.`
      };
    }

    // Check if all payment options have Square plan IDs (except FULL_PAY)
    const missingSquarePlans = typeData.paymentOptions.some((option: any) => {
      // Skip validation for FULL_PAY plans as they don't need Square integration
      if (option.planType === 'FULL_PAY') {
        return false;
      }
      
      // For all other plan types, require squarePlanId and slug
      return !option.squarePlanId || 
             option.squarePlanId.trim() === '' ||
             !option.slug || 
             option.slug.trim() === '';
    });

    if (missingSquarePlans) {
      return {
        isValid: false,
        error: `${type} program type has payment options without Square plans. Please create Square payment plans first.`
      };
    }
  }

  return { isValid: true };
};

// Step interface
interface Step {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  required: boolean;
  programTypeSpecific?: boolean;
}

// Step configuration for V2
const FORM_STEPS_V2: Step[] = [
  {
    id: "content",
    title: "Content",
    component: ContentStepV2,
    required: true,
    programTypeSpecific: true,
  },
  {
    id: "pricing",
    title: "Pricing",
    component: PricingStepV2,
    required: true,
    programTypeSpecific: true,
  },
  {
    id: "schedule",
    title: "Schedule",
    component: ScheduleStepV2,
    required: true,
    programTypeSpecific: true,
  },
  {
    id: "marketing",
    title: "Marketing",
    component: MarketingStepV2,
    required: false,
    programTypeSpecific: true,
  },
  {
    id: "review",
    title: "Review",
    component: ReviewStepV2,
    required: true,
    programTypeSpecific: true,
  },
];

interface MultiStepProgramFormProps {
  onSubmit: (data: any) => Promise<void>;
  draft?: any;
  draftId?: string;
  hideCommonFields?: boolean;
  isEditMode?: boolean;
  existingData?: any;
  onFieldChange?: (fieldPath: string) => void;
}

export const MultiStepProgramForm: React.FC<MultiStepProgramFormProps> = ({
  onSubmit,
  draft,
  isEditMode = false,
  existingData,
  onFieldChange,
}) => {
  // Core state
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removeTypeTarget, setRemoveTypeTarget] = useState<DeliveryModeV2 | null>(null);
  
  // Zustand store hooks
  const {
    activeProgramType,
    programTypes,
    updateProgramType,
    setActiveProgramType,
    removeProgramType,
    reset,
    loadedProgramId,
    draftId
  } = useProgramStore();

  const getProgramTypeLabel = useCallback((type: DeliveryModeV2) => {
    return type === "hybrid"
      ? "Hybrid"
      : type === "via-zoom"
        ? "Via-Zoom"
        : type === "on-campus"
          ? "On-Campus"
          : "Online";
  }, []);

  // Initialize with existing data in edit mode
  useEffect(() => {
    if (isEditMode && existingData) {
      console.log('🔍 DEBUG - Initializing with existing data:', existingData);
      
      // Set the active program type if available
      if (existingData.programType) {
        setActiveProgramType(existingData.programType);
      }
      
      // Initialize program types with existing data
      if (existingData.programTypes && Array.isArray(existingData.programTypes)) {
        existingData.programTypes.forEach((programType: any) => {
          if (programType.type) {
            updateProgramType(programType.type, programType);
          }
        });
      }
    }
  }, [isEditMode, existingData, setActiveProgramType, updateProgramType]);

  // Available steps based on configuration
  const availableSteps = useMemo(() => {
    return FORM_STEPS_V2;
  }, []);

  // Debug: Log program types on mount and when they change
  useEffect(() => {
    console.log('🔍 DEBUG - Program Types:', programTypes);
    console.log('🔍 DEBUG - Active Program Type:', activeProgramType);
  }, [programTypes, activeProgramType]);

  // Step navigation handlers
  const handleNextStep = useCallback(() => {
    if (currentStep < availableSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, availableSteps.length]);

  const handlePreviousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Step data update handler
  const handleStepDataUpdate = useCallback((data: any) => {
    console.log('🔍 DEBUG - handleStepDataUpdate called with:', data);
    console.log('🔍 DEBUG - Active program type:', activeProgramType);
    updateProgramType(activeProgramType, data);
    
    // Mark program type fields as dirty
    if (onFieldChange) {
      Object.keys(data).forEach(field => {
        onFieldChange(`programTypes.${activeProgramType}.${field}`);
      });
    }
  }, [updateProgramType, activeProgramType, onFieldChange]);

  // Copy handler that delegates to the appropriate step-specific copy logic
  const handleCopyStepData = useCallback((sourceType: DeliveryModeV2, sourceData: any) => {
    const currentStepInfo = availableSteps[currentStep];
    if (!currentStepInfo) return;

    const copiedData: any = {};
    let copiedFields: string[] = [];

    // Handle copy based on current step
    switch (currentStepInfo.id) {
      case 'content':
        // Copy content-specific fields
        if (sourceData?.subtitle) {
          copiedData.subtitle = sourceData.subtitle;
          copiedFields.push('subtitle');
        }
        if (sourceData?.about) {
          copiedData.about = sourceData.about;
          copiedFields.push('description');
        }
        if (sourceData?.videoUrl) {
          copiedData.videoUrl = sourceData.videoUrl;
          copiedFields.push('video URL');
        }
        if (sourceData?.nationalCertifications) {
          copiedData.nationalCertifications = { ...sourceData.nationalCertifications };
          copiedFields.push('certifications');
        }
        if (sourceData?.podcastData && Array.isArray(sourceData.podcastData)) {
          copiedData.podcastData = [...sourceData.podcastData];
          copiedFields.push('podcasts');
        }
        if (sourceData?.keyFeatures && Array.isArray(sourceData.keyFeatures)) {
          copiedData.keyFeatures = [...sourceData.keyFeatures];
          copiedFields.push('key features');
        }
        if (sourceData?.thisCourseIncludesData && Array.isArray(sourceData.thisCourseIncludesData)) {
          copiedData.thisCourseIncludesData = [...sourceData.thisCourseIncludesData];
          copiedFields.push('course includes');
        }
        if (sourceData?.programComparison && Array.isArray(sourceData.programComparison)) {
          copiedData.programComparison = [...sourceData.programComparison];
          copiedFields.push('program comparison');
        }
        break;
      
      case 'marketing':
        if (sourceData?.faqs && Array.isArray(sourceData.faqs)) {
          copiedData.faqs = [...sourceData.faqs];
          copiedFields.push('FAQs');
        }
        if (sourceData?.workPlaces && Array.isArray(sourceData.workPlaces)) {
          copiedData.workPlaces = [...sourceData.workPlaces];
          copiedFields.push('work places');
        }
        break;
      
      case 'schedule':
        if (sourceData?.schedule) {
          copiedData.schedule = { ...sourceData.schedule };
          copiedFields.push('schedule');
        }
        break;
      
      case 'pricing':
        if (sourceData?.paymentOptions && Array.isArray(sourceData.paymentOptions)) {
          copiedData.paymentOptions = [...sourceData.paymentOptions];
          copiedFields.push('payment options');
        }
        break;
    }

    if (copiedFields.length > 0) {
      handleStepDataUpdate(copiedData);
      toast.success(`Copied ${copiedFields.join(', ')} from ${sourceType}`);
    } else {
      toast.error(`No ${currentStepInfo.title.toLowerCase()} data found in ${sourceType} program type`);
    }
  }, [currentStep, availableSteps, handleStepDataUpdate]);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    
    try {
      // Validate Square plans are created before submission
      const squarePlanValidation = validateSquarePlans(programTypes);
      if (!squarePlanValidation.isValid) {
        toast.error(squarePlanValidation.error);
        setIsSubmitting(false);
        return;
      }

      // Get complete form data directly from the store
      const completeFormData = {
        ...draft,
        ...pickProgramCommonFieldsForSave(useProgramStore.getState()),
        activeProgramType,
        programType: activeProgramType,
        // Pass programTypes in the correct format
        programTypes: Object.entries(programTypes).reduce((acc, [type, data]) => {
          acc[type] = {
            programType: type,
            delivery: type === "hybrid" ? "Hybrid" : 
                     type === "via-zoom" ? "Via-Zoom" : 
                     type === "on-campus" ? "On-Campus" : "Online",
            ...data
          };
          return acc;
        }, {} as any)
      };
      
      // Transform to API format before submit using V2 function
      const apiPayload = transformToAPIFormatV2(completeFormData as any);
      console.log('📤 Final Submit Payload:', apiPayload);
      await onSubmit(apiPayload);
      
      // Success handling is done in the parent component (Step 2)
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to create program');
      // Error handling is done in the parent component (Step 2)
    } finally {
      setIsSubmitting(false);
    }
  }, [draft, activeProgramType, programTypes, onSubmit, reset]);

  // Get complete form data for all steps
  const completeData = useMemo(() => {
    return {
      ...draft,
      activeProgramType,
      programType: activeProgramType,
      programTypes,
      // Include programId/draftId for payment plan creation
      // Backend handles: active programs (by programId), inactive programs (by programId), or drafts (by draftId)
      loadedProgramId,
      draftId,
      programId: loadedProgramId || draftId || draft?.programId,
      // Transform programTypes to the format expected by step components
      programTypeDataMap: Object.entries(programTypes).reduce((acc, [type, data]) => {
        acc[type] = {
          programType: type,
          delivery: type === "hybrid" ? "Hybrid" : 
                   type === "via-zoom" ? "Via-Zoom" : 
                   type === "on-campus" ? "On-Campus" : "Online",
          ...data
        };
        return acc;
      }, {} as any)
    };
  }, [draft, activeProgramType, programTypes, loadedProgramId, draftId]);

  // Render current step
  const renderCurrentStep = useMemo(() => {
    const currentStepInfo = availableSteps[currentStep];
    if (!currentStepInfo) return null;
    
    const StepComponent = currentStepInfo.component;
    const currentProgramTypeData = programTypes[activeProgramType] || {};

    console.log('🔍 DEBUG - Rendering step:', currentStepInfo.title);
    console.log('🔍 DEBUG - Current program type data:', currentProgramTypeData);
    console.log('🔍 DEBUG - Active program type:', activeProgramType);

    return (
      <StepComponent
        data={currentProgramTypeData}
        onUpdate={handleStepDataUpdate}
        programType={activeProgramType}
        formData={completeData}
        isEditMode={isEditMode}
        onFieldChange={onFieldChange}
        activeProgramType={activeProgramType}
        programTypes={programTypes}
      />
    );
  }, [currentStep, availableSteps, programTypes, activeProgramType, completeData, isEditMode, handleStepDataUpdate]);

  if (false) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <AlertDialog
        open={Boolean(removeTypeTarget)}
        onOpenChange={(open) => {
          if (!open) setRemoveTypeTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {removeTypeTarget ? getProgramTypeLabel(removeTypeTarget) : ""} program type?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all the data entered for this program type (content, pricing, schedule, marketing, etc.).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!removeTypeTarget) return;
                const label = getProgramTypeLabel(removeTypeTarget);
                removeProgramType(removeTypeTarget);
                setRemoveTypeTarget(null);
                if (onFieldChange) onFieldChange("programTypes");
                toast.success(`Removed ${label} program type`);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

        {/* Program Type Selector */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Program Types</h3>
          </div>
          
          {/* Add Program Type Dropdown */}
          <div className="relative">
            <select
              onChange={(e) => {
                        const newType = e.target.value as DeliveryModeV2;
                if (newType && !programTypes[newType]) {
                  setActiveProgramType(newType);
                  toast.success(`Added ${newType === "hybrid" ? "Hybrid" : newType === "via-zoom" ? "Via-Zoom" : newType === "on-campus" ? "On-Campus" : "Online"} program type`);
                }
                e.target.value = ''; // Reset selection
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              defaultValue=""
            >
              <option value="" disabled>+ Add Program Type</option>
                      {(['hybrid', 'via-zoom', 'on-campus', 'online'] as DeliveryModeV2[])
                .filter(type => !programTypes[type])
                .map((type) => (
                  <option key={type} value={type}>
                    {type === "hybrid" ? "Hybrid" : type === "via-zoom" ? "Via-Zoom" : type === "on-campus" ? "On-Campus" : "Online"}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Configured Program Types */}
        {Object.keys(programTypes).length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p className="text-lg font-medium mb-2">No Program Types Added</p>
            <p className="text-sm">Add a program type to start configuring your program</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(programTypes).map(([type]) => (
              <div
                key={type}
                className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${
                  activeProgramType === type
                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {/* Remove Button */}
                <button
                        onClick={() => {
                          setRemoveTypeTarget(type as DeliveryModeV2);
                        }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors duration-200"
                  title="Remove program type"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Program Type Card */}
                        <button
                          onClick={() => setActiveProgramType(type as DeliveryModeV2)}
                          className="w-full text-left"
                        >
                  <div className="text-center">
                    <div className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center ${
                      activeProgramType === type ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {type === "hybrid" && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                        </svg>
                      )}
                      {type === "via-zoom" && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      )}
                      {type === "on-campus" && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {type === "online" && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.666.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="text-sm font-medium mb-1">
                      {type === "hybrid" ? "Hybrid" : type === "via-zoom" ? "Via-Zoom" : type === "on-campus" ? "On-Campus" : "Online"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {activeProgramType === type ? 'Active' : 'Click to configure'}
                    </div>
                  </div>
                </button>

                {/* Active Indicator */}
                {activeProgramType === type && (
                  <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-500 rounded-full"></div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

        {/* Step Navigation */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Configuration Steps</h3>
        </div>
        
        {/* Step Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Step {currentStep + 1} of {availableSteps.length}</span>
            <span>{Math.round(((currentStep + 1) / availableSteps.length) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / availableSteps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Step Tabs */}
        <div className="flex flex-wrap gap-2">
          {availableSteps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(index)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
                currentStep === index
                  ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                currentStep === index ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
              }`}>
                {index + 1}
              </div>
              <span className="font-medium">{step.title}</span>
            </button>
          ))}
        </div>
      </div>

        {/* Current Step Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 min-h-[500px]">
        <div className="flex justify-between">

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {availableSteps[currentStep]?.title}
          </h2>
          <p className="text-gray-600">
            Configure the {availableSteps[currentStep]?.title.toLowerCase()} settings for your {activeProgramType === "hybrid" ? "Hybrid" : activeProgramType === "via-zoom" ? "Via-Zoom" : activeProgramType === "on-campus" ? "On-Campus" : "Online"} program.
          </p>
        </div>
        <div>
          {activeProgramType && programTypes && (
            <CopyFromProgramType
              currentStep={availableSteps[currentStep]?.id || 'content'}
              activeProgramType={activeProgramType}
              programTypes={programTypes}
              onCopy={handleCopyStepData}
            />
          )}
        </div>
        </div>
        
        <div className="space-y-6">
          {renderCurrentStep}
        </div>

        {/* Navigation Buttons - Moved to Bottom */}
        <div className="flex justify-between items-center pt-6 mt-8 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={handlePreviousStep}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
            {currentStep > 0 && (
              <span className="text-sm text-gray-500">
                {availableSteps[currentStep - 1]?.title}
              </span>
            )}
            
          </div>

          <div className="flex items-center gap-3">
            {currentStep < availableSteps.length - 1 && (
              <span className="text-sm text-gray-500">
                {availableSteps[currentStep + 1]?.title}
              </span>
            )}
            {currentStep === availableSteps.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Submit Program
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNextStep}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Next
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};